import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { alpha } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";

export const AddClassesToGroupDialog = ({
  open,
  setOpen,
  assignmentGroupId,
  onSuccess,
  hasUnpublishedAssignments = false,
}) => {
  const ApiClient = useApiClient();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [allClasses, setAllClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && assignmentGroupId) {
      fetchClasses();
      setSelectedClasses([]);
      setSearch("");
    }
  }, [open, assignmentGroupId]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const classesData =
        await ApiClient.assignment.getClassesForAssignmentGroup({
          assignmentGroup: assignmentGroupId,
        });
      setAllClasses(classesData);
    } catch (error) {
      console.error("Failed to fetch classes", error);
    } finally {
      setLoading(false);
    }
  };

  const availableClasses = useMemo(() => {
    return allClasses
      .filter(
        (c) =>
          !selectedClasses.some((s) => s.schoolClass.id === c.schoolClass.id),
      )
      .filter((c) =>
        search
          ? c.schoolClass.name.toLowerCase().includes(search.toLowerCase())
          : true,
      );
  }, [allClasses, selectedClasses, search]);

  const handleClose = () => {
    setOpen(false);
    setSelectedClasses([]);
    setSearch("");
  };

  const handleSelect = (classData) => {
    setSelectedClasses([...selectedClasses, classData]);
  };

  const handleRemove = (classData) => {
    setSelectedClasses(
      selectedClasses.filter(
        (s) => s.schoolClass.id !== classData.schoolClass.id,
      ),
    );
  };

  const handleSubmit = async () => {
    if (selectedClasses.length === 0) return;

    setSubmitting(true);
    try {
      const result = await ApiClient.assignment.addClassesToGroup({
        assignmentGroup: assignmentGroupId,
        schoolClasses: selectedClasses.map((c) => c.schoolClass.id),
      });

      if (result.success) {
        onSuccess(result);
        handleClose();
        enqueueSnackbar("Successfully added classes to assignment group", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(result.message, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to add classes to group", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Add more classes</DialogTitle>
      <DialogContent>
        {hasUnpublishedAssignments ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            All assignments in this group must be published before you can add
            more classes. Please publish any draft assignments first.
          </Alert>
        ) : (
          <Typography sx={{ mb: 2 }}>
            Select classes to add to this assignment group. Assignments will be
            created for classes that don&apos;t have one yet.
          </Typography>
        )}

        {loading ? (
          <GeneralLoading />
        ) : hasUnpublishedAssignments ? (
          <Card variant="background">
            <CardContent>
              <Typography variant="subtitle1">
                Please publish all draft assignments before adding more classes.
              </Typography>
            </CardContent>
          </Card>
        ) : allClasses.length === 0 ? (
          <Card variant="background">
            <CardContent>
              <Typography variant="subtitle1">
                No classes available to add
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Selected Classes Section */}
            {selectedClasses.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Selected Classes
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: "auto", mb: 2 }}>
                  {selectedClasses.map((classData) => (
                    <Card
                      key={classData.schoolClass.id}
                      sx={{
                        mt: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      }}
                    >
                      <CardContent sx={{ p: 1.5 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Stack direction="column">
                              <Typography
                                variant="subtitle1"
                                fontWeight="medium"
                              >
                                {classData.schoolClass.name}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {classData.schoolClass.school.name}
                              </Typography>
                            </Stack>

                            <Chip
                              size="small"
                              label={`Year ${classData.schoolClass.yearGroup.yearGroup}`}
                            />

                            <Chip
                              size="small"
                              label={
                                classData.assignment
                                  ? "Assignment exists"
                                  : "No assignment"
                              }
                              color={
                                classData.assignment ? "success" : "default"
                              }
                              variant={
                                classData.assignment ? "filled" : "outlined"
                              }
                            />
                          </Stack>

                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleRemove(classData)}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </>
            )}

            {/* Available Classes Section */}
            <Typography variant="h6" sx={{ mt: 2 }}>
              Available Classes
            </Typography>
            <TextField
              label="Search for class"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ my: 2 }}
              fullWidth
            />

            <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
              {availableClasses.map((classData) => (
                <Card
                  key={classData.schoolClass.id}
                  sx={{
                    mt: 1,
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Stack direction="column">
                          <Typography variant="subtitle1" fontWeight="medium">
                            {classData.schoolClass.name}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {classData.schoolClass.school.name}
                          </Typography>
                        </Stack>
                        <Chip
                          size="small"
                          label={`Year ${classData.schoolClass.yearGroup.yearGroup}`}
                        />
                        <Chip
                          size="small"
                          label={
                            classData.hasAssignment
                              ? "Assignment exists"
                              : "No assignment"
                          }
                          color={
                            classData.hasAssignment ? "success" : "default"
                          }
                          variant={
                            classData.hasAssignment ? "filled" : "outlined"
                          }
                        />
                      </Stack>
                      <Button
                        variant="contained"
                        onClick={() => handleSelect(classData)}
                      >
                        Select
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={selectedClasses.length === 0 || submitting}
          >
            {submitting
              ? "Adding..."
              : `Add ${selectedClasses.length} class${selectedClasses.length !== 1 ? "es" : ""}`}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
